import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { NgbDateParserFormatter, NgbDateStruct, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap'
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import { isEqual } from 'lodash-es'
import { debounceTime, merge, Subject, Subscription } from 'rxjs'
import { LocaleAwareNgbDateParserFormatter } from '../../vendors/ngb.vendor.module'
import { v4 as uuidv4 } from 'uuid'

const isInputChangingKey: (key:string) => boolean 
  = (key) => [ '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'Backspace', 'Delete' ].includes(key)

@Component({
    selector: 'app-datetime-range-input',
    templateUrl: './datetime-range-input.component.html',
    providers: [
        { provide: NgbDateParserFormatter, useClass: LocaleAwareNgbDateParserFormatter }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DatetimeRangeInputComponent implements OnChanges {
  @Input() readonly = false
  isDateRangeJoined = false
  joinDateRangeCheckBoxId = uuidv4()

  @Input() pastReference: Date | undefined | null

  _enableJoinedDateBehavior = false
  @Input() set enableJoinedDateBehavior (isEnabled: boolean) {
    // filter only the first time this is set
    if(isEnabled && !this._enableJoinedDateBehavior) {
      this._enableJoinedDateBehavior = true
      this.isDateRangeJoined = true
      this.joinDateRange()
    }
  }
  get enableJoinedDateBehavior (): boolean {
    return this._enableJoinedDateBehavior
  }

  @Input() timezone: string
  @Input() dateFormat: string

  _minDateAsNgb: NgbDateStruct | undefined
  @Input() minDate: Date | undefined

  _maxDateAsNgb: NgbDateStruct | undefined
  @Input() maxDate: Date | undefined

  @Input() timeStep = 10

  @Input() zonedDateRange: [Date, Date] | null | undefined

  zonedDateRangeForm = new FormGroup({
    start: new FormGroup({
      date: new FormControl<NgbDateStruct>({ year: NaN, month: NaN, day: NaN, }, { nonNullable: true }),
      time: new FormControl<NgbTimeStruct>({ hour: NaN, minute: NaN, second: NaN, }, { nonNullable: true })
    }),
    end: new FormGroup({
      date: new FormControl<NgbDateStruct>({ year: NaN, month: NaN, day: NaN, }, { nonNullable: true }),
      time: new FormControl<NgbTimeStruct>({ hour: NaN, minute: NaN, second: NaN, }, { nonNullable: true })
    }),
  })

  @Output() zonedDateRangeChange = new EventEmitter<[Date, Date]>();

  private startTimePickerInputTouched$ = new Subject<HTMLInputElement>()
  private endTimePickerInputTouched$ = new Subject<HTMLInputElement>()

  subscriptions: Subscription[] = [
    this.zonedDateRangeForm.valueChanges.subscribe(value => {
      // Allow disabled behavior through form control while getting the displayed value when joinedDateRange is enabled
      const start = this.zonedDateRangeForm.controls.start.getRawValue()
      const end = value.end
      if(this.timezone === undefined) throw Error('timezone is undefined')
      if(!start || !end) return
      const zonedDateRange: [Date, Date] = [
        start.date && !isNaN(start.date.year) && !isNaN(start.date.month) && !isNaN(start.date.day)
        && start.time && !isNaN(start.time.hour) && !isNaN(start.time.minute)
          ? zonedTimeToUtc(
            `${start.date.year}-${start.date.month.toString().padStart(2, '0')}-${start.date.day.toString().padStart(2, '0')}T${start.time.hour.toString().padStart(2, '0')}:${(start.time.minute - (start.time.minute % this.timeStep)).toString().padStart(2, '0')}:00.000`,
            this.timezone
          )
          : new Date('invalid date'),
        end.date && !isNaN(end.date.year) && !isNaN(end.date.month) && !isNaN(end.date.day)
          && end.time && !isNaN(end.time.hour) && !isNaN(end.time.minute)
          ? zonedTimeToUtc(
            `${end.date.year}-${end.date.month.toString().padStart(2, '0')}-${end.date.day.toString().padStart(2, '0')}T${end.time.hour.toString().padStart(2, '0')}:${(end.time.minute - (end.time.minute % this.timeStep)).toString().padStart(2, '0')}:00.000`,
            this.timezone
          )
          : new Date('invalid date')
      ]
      this.zonedDateRangeChange.emit(zonedDateRange)
    }),
    merge(
      this.startTimePickerInputTouched$.pipe(debounceTime(400)),
      this.endTimePickerInputTouched$.pipe(debounceTime(400)),
    ).subscribe((inputElement)=>{
      // simulate blur event so that nbg timepicker apply its on blur handler (ie coerce value to a valid time and update forme control vaue)
      // only do it if the target element is the active element to avoid refocusing an element that already has lost focus by other means during the debounce delay
      if (inputElement === document.activeElement){
        inputElement.blur()
        inputElement.focus()
      }
    })
  ]

  ngOnDestroy (): void {
    this.subscriptions.forEach(sub => sub.unsubscribe())
  }

  ngOnChanges (changes: SimpleChanges): void {
    if(!this.timezone) throw Error('timezone undefined')
    if(changes.minDate || changes.maxDate){
      this.rangeDateUpdate()
    }
    if(changes.zonedDateRange) {
      const dateRange = changes.zonedDateRange.currentValue
      if(!this.timezone) throw Error('timezone undefined')
      if(dateRange === undefined || dateRange === null) {
        this.zonedDateRangeForm.setValue({
          start: {
            date: { year: NaN, month: NaN, day: NaN, },
            time: { hour: NaN, minute: NaN, second: NaN, }
          },
          end: {
            date: { year: NaN, month: NaN, day: NaN, },
            time: { hour: NaN, minute: NaN, second: NaN, }
          },
        })
      } else if(isNaN(dateRange[0].valueOf()) || isNaN(dateRange[1].valueOf())) {
        return
      } else {
        const zonedDate0 = utcToZonedTime(dateRange[0].valueOf(), this.timezone)
        const zonedDate1 = utcToZonedTime(dateRange[1].valueOf(), this.timezone)

        const formValueFromInputs = {
          start: {
            date: { year: zonedDate0.getFullYear(), month: zonedDate0.getMonth() + 1, day: zonedDate0.getDate(), },
            time: { hour: zonedDate0.getHours(), minute: zonedDate0.getMinutes(), second: 0, }
          },
          end: {
            date: { year: zonedDate1.getFullYear(), month: zonedDate1.getMonth() + 1, day: zonedDate1.getDate(), },
            time: { hour: zonedDate1.getHours(), minute: zonedDate1.getMinutes(), second: 0, }
          },
        }
        if(!isEqual(this.zonedDateRangeForm.getRawValue(), formValueFromInputs)) {
          this.zonedDateRangeForm.setValue(formValueFromInputs)
        }
      }
    }
    if(changes.pastReference) {
      this.joinDateRange()
    }
    if(changes.timeStep && !(Number.isInteger(changes.timeStep.currentValue) && changes.timeStep.currentValue <= 60)) throw `invalid timeStep ${changes.timeStep.currentValue}, expected integer between 0 and 60`
  }

  private rangeDateUpdate () {
    if (this.minDate === undefined || this.minDate === null || isNaN(this.minDate.valueOf())) {
      this._minDateAsNgb = {
        year : NaN,
        month: NaN,
        day  : NaN,
      }
    } else {
      const zonedMinDate = utcToZonedTime(this.minDate.valueOf(), this.timezone)
      this._minDateAsNgb = {
        year : zonedMinDate.getFullYear(),
        month: zonedMinDate.getMonth() + 1,
        day  : zonedMinDate.getDate(),
      }
    }
    if (this.maxDate === undefined || this.maxDate === null || isNaN(this.maxDate.valueOf())) {
      this._maxDateAsNgb = {
        year : NaN,
        month: NaN,
        day  : NaN,
      }
    } else {
      const zonedMaxDate = utcToZonedTime(this.maxDate.valueOf(), this.timezone)
      this._maxDateAsNgb = {
        year : zonedMaxDate.getFullYear(),
        month: zonedMaxDate.getMonth() + 1,
        day  : zonedMaxDate.getDate(),
      }
    }
  }

  setDateToNow (): void {
    if(this.timezone === undefined) throw Error('timezone is undefined')
    const now = utcToZonedTime(Date.now(), this.timezone)
    this.zonedDateRangeForm.controls.end.setValue({
      date: {
        year : now.getFullYear(),
        month: now.getMonth() + 1,
        day  : now.getDate(),
      },
      time: {
        hour  : now.getHours(),
        minute: now.getMinutes() - (now.getMinutes() % this.timeStep),
        second: 0,
      }
    })
  }
  joinDateRange (): void {
    if(this.timezone === undefined) throw Error('timezone is undefined')
    if(this.pastReference === undefined) throw Error('pastReference is undefined')
    if(this.pastReference === null) throw Error('pastReference is null')
    if(this.isDateRangeJoined) {
      this.zonedDateRangeForm.controls.start.disable()
      const pastReference = utcToZonedTime(this.pastReference.valueOf(), this.timezone)
      this.zonedDateRangeForm.controls.start.setValue({
        date: {
          year : pastReference.getFullYear(),
          month: pastReference.getMonth() + 1,
          day  : pastReference.getDate(),
        },
        time: {
          hour  : pastReference.getHours(),
          minute: pastReference.getMinutes() - (pastReference.getMinutes() % this.timeStep),
          second: 0,
        }
      })
    } else {
      this.zonedDateRangeForm.controls.start.enable()
    }
  }
  handleStartTimePickerKeyUpEvent (event): void{
    if (!(event instanceof KeyboardEvent && event.type === 'keyup')){
      throw Error('this action is expected to be trigged by keyup events only')
    }
    if (!(event.target instanceof HTMLInputElement)){
      throw Error('this action is expected to be trigged by event withinput element target only')
    }
    if (isInputChangingKey(event.key)){
      this.startTimePickerInputTouched$.next(event.target)
    }
  }
  handleEndTimePickerKeyUpEvent (event): void{
    if (!(event instanceof KeyboardEvent && event.type === 'keyup')){
      throw Error('this action is expected to be trigged by keyup events only')
    }
    if (!(event.target instanceof HTMLInputElement)){
      throw Error('this action is expected to be trigged by event withinput element target only')
    }
    if (isInputChangingKey(event.key)){
      this.endTimePickerInputTouched$.next(event.target)
    }
  }
}
